import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PageEvent } from '@angular/material/paginator';
import { firstValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import {
  FilterGroup,
  TableFilterBarComponent,
  TypeaheadItem,
} from '@custom-components/general/table-filter-bar';
import { FilledButtonComponent, OutlinedButtonComponent } from '@custom-components/general/button';
import { DateFieldComponent } from '@custom-components/general/form/date-field/date-field.component';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import {
  APP_PATHS,
  INSTALLATION_GROUPS_TABLE_COLUMNS_CONFIG,
  PAGINATION_DEFAULT,
  XEMEX_OID,
} from '@core/constants';
import { getUserActivePartner, InstallationGroupFilterModel } from '@core/models';
import { CompanyService } from '@core/services';
import { InstallationGroupStore } from '@root/app/store/installation-group.store';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import {
  CreateInstallationGroupDialogComponent,
  CreateInstallationGroupResult,
} from '../../components/create-installation-group-dialog/create-installation-group-dialog.component';

@Component({
  selector: 'xemex-installation-groups-page',
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    TableComponent,
    TableFilterBarComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    DateFieldComponent,
    PageHeaderComponent,
    PageBodyComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './installation-groups-page.component.html',
})
export class InstallationGroupsPageComponent {
  readonly installationGroupStore = inject(InstallationGroupStore);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly authStore = inject(AuthStore);
  private readonly userStore = inject(UserStore);
  private readonly companyService = inject(CompanyService);

  /** Partner / date filters are only offered to Xemex users, who can see every partner's groups. */
  protected readonly isXemexUser = computed(
    () => getUserActivePartner(this.userStore.user())?.partnerOid === XEMEX_OID,
  );

  /** True when the user may create installation groups. */
  protected readonly canManage = computed(() => {
    const partnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
    const features =
      this.authStore
        .permissions()
        ?.filter(p => p.groupObjectId == partnerOid || p.groupObjectId == XEMEX_OID)
        .flatMap(p => p.roles.flatMap(role => role.features.flatMap(f => f.features)))
        .filter(f => f.featureEnabled)
        .map(f => f.featureName) ?? [];
    return features.includes('manage_installation_groups');
  });

  protected readonly columns = INSTALLATION_GROUPS_TABLE_COLUMNS_CONFIG;
  protected readonly breadcrumbs: BreadcrumbItem[] = [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    { label: this.translate.instant('sidebarMenu.installationGroups') },
  ];

  /** Async data source handed to the filter-bar partner typeahead pill. */
  protected readonly partnerSearch = (query: string): Promise<TypeaheadItem[]> =>
    firstValueFrom(
      this.companyService.getListCompaniesFilter({ limit: 10, offset: 0, partnerName: query }),
    ).then(res => (res.partners ?? []).map(p => ({ id: p.partnerOid, label: p.partnerName })));

  protected readonly filterGroups = computed<FilterGroup[]>(() => [
    {
      key: 'partner',
      label: this.translate.instant('installationGroups.filter.partner'),
      type: 'typeahead',
      selection: 'single',
      search: this.partnerSearch,
      placeholder: this.translate.instant('general.placeholderInput'),
    },
  ]);

  /** Owner-partner / date-range filters currently applied (excludes pagination). */
  private partnerOid: string | undefined;
  /** Datepicker-bound values; only promoted to {@link appliedDateRange} on "Apply". */
  protected pendingCreatedAfter: Date | undefined;
  protected pendingCreatedBefore: Date | undefined;
  private readonly appliedDateRange = signal<{ after?: Date; before?: Date }>({});

  protected readonly hasActiveDateRange = computed(
    () => !!(this.appliedDateRange().after || this.appliedDateRange().before),
  );

  protected readonly tableData = computed(
    () =>
      this.installationGroupStore.data().installationGroups as unknown as Record<string, unknown>[],
  );
  protected readonly totalCount = computed(() => this.installationGroupStore.data().numberOfItems);

  protected readonly rowLink = (row: Record<string, unknown>) => [
    '/',
    APP_PATHS.InstallationGroups,
    row['installationGroupOid'] as string,
  ];

  constructor() {
    this.fetch(PAGINATION_DEFAULT.offset);
  }

  protected onPageChange(evt: PageEvent): void {
    this.fetch(evt.pageIndex);
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    this.partnerOid = filters['partner']?.[0] || undefined;
    this.fetch(PAGINATION_DEFAULT.offset);
  }

  protected onDateRangeApply(menuTrigger: MatMenuTrigger): void {
    this.appliedDateRange.set({
      after: this.pendingCreatedAfter,
      before: this.pendingCreatedBefore,
    });
    this.fetch(PAGINATION_DEFAULT.offset);
    menuTrigger.closeMenu();
  }

  protected onDateRangeClear(menuTrigger: MatMenuTrigger): void {
    this.pendingCreatedAfter = undefined;
    this.pendingCreatedBefore = undefined;
    this.appliedDateRange.set({});
    this.fetch(PAGINATION_DEFAULT.offset);
    menuTrigger.closeMenu();
  }

  /** Builds the current filter (Xemex-only fields included only for Xemex users) and fetches. */
  private fetch(offset: number): void {
    const filter: InstallationGroupFilterModel = {
      limit: PAGINATION_DEFAULT.limit,
      offset,
    };
    if (this.isXemexUser()) {
      const { after, before } = this.appliedDateRange();
      // Span the whole day: created on or after the start of "after", up to the end of "before".
      const from = after ? new Date(after) : undefined;
      const to = before ? new Date(before) : undefined;
      from?.setHours(0, 0, 0, 0);
      to?.setHours(23, 59, 59, 999);
      filter.partnerOid = this.partnerOid;
      filter.createdAfter = from?.toISOString();
      filter.createdBefore = to?.toISOString();
    }
    this.installationGroupStore.getInstallationGroups(filter);
  }

  protected openCreateDialog(): void {
    this.dialog
      .open(CreateInstallationGroupDialogComponent, {
        width: '32rem',
        maxWidth: 'calc(100vw - 2rem)',
      })
      .afterClosed()
      .subscribe((result: CreateInstallationGroupResult | undefined) => {
        if (result) {
          this.installationGroupStore.createInstallationGroup(result);
        }
      });
  }
}
